import { isEmpty, result, extend } from 'underscore';
import Promise from 'bluebird';
import { assign } from 'xstate';
import { withScope } from '@bingads-webui-react/scope-service';
import { stepStateMachine } from '@bingads-webui-campaign-react/step-wizard-machine';
import { getVerifiedTrackingSettingWrite as viewModelToBulkModelForVerifiedTrackingSettings } from '@bingads-webui-campaign-react/third-party-measurement';
import { ScopeLevel } from '@bingads-webui-campaign-react/constants';
import exclusionRepository from '@bingads-webui-campaign-legacy/advanced-settings/util/exclusion-repository';
import { saveCampaign, getErrorsPlaceholders } from '../../model/campaign-bulk';
import { STEP_PATHS, getInitialCampaignViewModel } from '../../common/constants';
import { StepCampaignSettings } from './step-campaign-settings';
import { viewModelToModelForCampaign, loadCampaign, modelToViewModelForCampaign, getDealDetails, fillLocationForDeal } from '../../model/campaign';

const getCampaignId = context => result(context, ['data', 'campaign', 'data', 'Id']) || result(context, ['stepsData', 'campaignId']);

const needsLoading = () => true;

/**
 * Loads step data asynchronously. The returned promise must resolve
 * into a single object, opaque to the step, which will be saved
 * in the context under the `data` property.
 *
 * The function is called when the wizard enters the step.
 * The step will be blocked until the promise resolves.
 * @param {Object} context - the current context
 * @returns {Promise<Object>} a promise that must resolve
 * into a single object, opaque to the step, which will be saved
 * in the context under the `data` property.
 */
const loadData = (context) => {
  const {
    cid,
    aid,
    i18n,
    xandrVideoCampaignType,
    xandrVideoDeal,
    serverContext,
    isDisplayAds,
  } = context.stepsData;
  const campaignId = getCampaignId(context);
  let loadPromises = {
    campaign: Promise.resolve(getInitialCampaignViewModel(i18n, serverContext.CurrentAccount.CurrencyCode, {
      xandrVideoDeal,
      xandrVideoCampaignType,
      isDisplayAds,
      serverContext,
    })),
  };
  if (campaignId) {
    loadPromises = extend(loadPromises, {
      campaign: loadCampaign(cid, aid, campaignId),
      dealDetails: getDealDetails(cid, aid, campaignId, serverContext),
      exclusions: exclusionRepository.load(campaignId, null, ScopeLevel.Campaign),
    });
  } else {
    loadPromises = extend(loadPromises, {
      dealDetails: fillLocationForDeal(xandrVideoDeal, serverContext),
    });
  }

  return Promise.props(loadPromises).then(({
    campaign,
    dealDetails,
    exclusions: [campaignExclusions] = [null],
  }) => {
    let results = {
      campaign,
      ...getErrorsPlaceholders(),
    };
    if (campaignId) {
      const campaignData = modelToViewModelForCampaign({
        campaignModel: campaign,
        dealDetails,
        exclusions: {
          campaignExclusions,
        },
      });
      results = extend(results, {
        campaign: campaignData,
      });
    } else {
      results = extend(results, {
        campaign: {
          ...campaign,
          data: {
            ...campaign.data,
            deal: dealDetails,
          },
        },
      });
    }
    return results;
  });
};

// must return `Promise.resolve(data)` or `Promise.reject(error)`
// the payload will be available in `onDone` and `onError` in event's `data` prop.
const saveData = context =>
  saveCampaign({
    campaign: context.data.campaign,
    viewModelToBulkModelForVerifiedTrackingSettings,
  }, {
    scenario: context.stepsData.scenarioName,
    activityLogger: context.stepsData.scenario.createActivity('saveCampaignAndLinkedData'),
    i18n: context.stepsData.i18n,
  });

const isValid = context => context.data.campaign.isValid;
const isDirty = context => context.data.campaign.isDirty;
const hasServerErrors = context => !isEmpty(context.data.serverErrors) || !isEmpty(context.data.systemErrors);
export const needsSaving = context => isDirty(context) || hasServerErrors(context) || !context.data.campaign.data.Id;

const patchStepsData = assign({
  stepsData: (context/* , event */) => ({
    ...context.stepsData,
    campaign: {
      ...viewModelToModelForCampaign(context.data.campaign),
      deal: context.data.campaign.data.deal,
    },
  }),
});
const clearErrors = assign({
  data: context => ({
    ...context.data,
    ...getErrorsPlaceholders(),
  }),
});
const hasIncomingSystemError = () => false;

export const stepCampaignSettingsWizardConfig = {
  id: STEP_PATHS.CAMPAIGN_SETTINGS,
  title: _TL_('Campaign settings'),
  StepMachine: stepStateMachine,
  StepMachineOptions: (stepInfo, initialStepsData) => ({
    machineId: STEP_PATHS.CAMPAIGN_SETTINGS,
    initialContext: {
      id: stepInfo.id,
      stepsData: initialStepsData,
      stepInfo,
      data: {
        campaign: getInitialCampaignViewModel(
          initialStepsData.i18n,
          initialStepsData.serverContext.CurrentAccount.CurrencyCode,
          {
            xandrVideoCampaignType: initialStepsData.xandrVideoCampaignType,
            xandrVideoDeal: initialStepsData.xandrVideoDeal,
            isDisplayAds: initialStepsData.isDisplayAds,
          }
        ),
        ...getErrorsPlaceholders(),
      },
      invalidSaveAttempt: 0,
    },
    actions: {
      patchStepsData,
      clearErrors,
    },
    guards: {
      needsLoading,
      isValid,
      isDirty,
      needsSaving,
      hasIncomingSystemError,
    },
  }),
  StepComponent: withScope(StepCampaignSettings),
  stepRepositoryConfig: {
    saveData,
    loadData,
  },
};
